// src/middleware/auth.js
import { Navigate, useLocation } from "react-router-dom";
import { useRef } from "react";
import { authService } from "../services/api";
import { useNotification } from "../pages/shared/hooks/notification";

export const ProtectedRoute = ({ children, roles = [] }) => {
  const { showNotification } = useNotification();
  const location = useLocation();
  const hasShownRef = useRef(false); // 🧠 prevent repeated notifications

  // Step 1: Get stored auth data
  const userInfo = localStorage.getItem("jmc_user");
  const legacyToken = localStorage.getItem("jmc_token");

  if (userInfo) {
    try {
      const parsed = JSON.parse(userInfo);
      console.log("Parsed user:", parsed);
    } catch (e) {
      console.log("Failed to parse user data:", e);
    }
  }

  // Step 2: Auth check
  const isAuth = authService.isAuthenticated();
  const currentUser = authService.getCurrentUser();

  if (!isAuth) {
    console.log("❌ Not authenticated. Redirecting to login...");
    // Save attempted route so user can be redirected after login
    localStorage.setItem("redirect_after_login", location.pathname);
    return <Navigate to="/login" replace />;
  }

  // Step 3: Role-based permission check
  try {
    const parsedUser = currentUser || JSON.parse(userInfo || "{}");
    const userRole = parsedUser?.role?.toLowerCase?.();

    // If roles are restricted and user doesn't match → deny access
    if (roles.length > 0) {
      // If userRole is undefined, skip the check (user data still loading)
      if (!userRole) {
        console.log("⏳ User role not yet loaded, waiting...");
        return null; // Return null to prevent flash of wrong content
      }

      if (!roles.includes(userRole)) {
        if (!hasShownRef.current) {
          showNotification("You have no permission for this route.", "error");
          hasShownRef.current = true;
        }
        console.warn(`🚫 Access denied for role "${userRole}". Required: ${roles}`);
        return <Navigate to="/home" replace />;
      }
    }
  } catch (err) {
    console.error("Error checking role:", err);
  }

  console.log("✅ Access granted to:", location.pathname);
  return children;
};

// ✅ AuthRoute: Prevent logged-in users from seeing login/signup pages
export const AuthRoute = ({ children }) => {
  const isAuth = authService.isAuthenticated();

  if (isAuth) {
    const redirectTo = "/dashboard";
    console.log("🔁 Authenticated user redirected to:", redirectTo);
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};
